import { useFormik } from 'formik';
import React from 'react';
import { Button, Col, Form, FormFeedback, FormGroup, Row } from 'reactstrap';
import { validate } from 'utils/form-utils';
import * as Yup from 'yup';
import { FormikSelect } from 'components/inputs/FormikSelect';
import classNames from 'classnames';
import { getSelectOption, LANGUAGES_SELECT_OPTIONS, RATE_ENGLISH_SELECT_OPTIONS } from 'types/select-types';
import { LanguagesForm, languagesFormDefaults } from 'types/join-mindtrust.types';

const validationSchema = () =>
  Yup.object()
    .shape({
      rateEnglish: Yup.string().nullable().required('Rate your english is required'),
      nativeLanguage: Yup.string().nullable().notRequired(),
    })
    .required();

interface Props {
  step12?: LanguagesForm | null;
  onNext: (joinMindtrust: LanguagesForm) => void;
  goBack: () => void;
}

export const JoinMindTrust12: React.FC<Props> = ({ step12, goBack, onNext }) => {
  const formik = useFormik<LanguagesForm>({
    enableReinitialize: true,
    initialValues: step12 || languagesFormDefaults(),
    validateOnMount: true,
    onSubmit: onNext,
    validate: validate<LanguagesForm>(validationSchema),
  });
  const { values, touched, errors, isValid } = formik;

  return (
    <Form onSubmit={formik.handleSubmit}>
      <div className="flow-content">
        <h1>Languages</h1>
        <p>
          Effective communication is crucial for the success of our projects with US-based clients. Therefore, we kindly
          require applications to process at least a B1 level of proficiency in both written and spoken English.
        </p>
        <FormGroup>
          <label>
            How do you rate your English proficiency? <span className="error-message">*</span>
          </label>
          <FormikSelect
            className={classNames('form-input', {
              'is-invalid': touched.rateEnglish && !!errors[`rateEnglish`],
              'is-valid': touched.rateEnglish && !errors[`rateEnglish`],
            })}
            placeholder="Select your rate English"
            name="rateEnglish"
            value={getSelectOption(values.rateEnglish, RATE_ENGLISH_SELECT_OPTIONS) || RATE_ENGLISH_SELECT_OPTIONS[0]}
            options={RATE_ENGLISH_SELECT_OPTIONS}
            setFieldTouched={formik.setFieldTouched}
            setFieldValue={formik.setFieldValue}
          />
          <i className="glyphicon glyphicon-user form-control-feedback" />
          <FormFeedback>{errors.rateEnglish}</FormFeedback>
        </FormGroup>
        <FormGroup>
          <label>Native language (mother tongue)</label>
          <FormikSelect
            className={classNames('form-input', {
              'is-invalid': touched.nativeLanguage && !!errors[`nativeLanguage`],
              'is-valid': touched.nativeLanguage && !errors[`nativeLanguage`],
            })}
            placeholder="Select your nativeLanguage"
            name="nativeLanguage"
            value={getSelectOption(values.nativeLanguage, LANGUAGES_SELECT_OPTIONS) || LANGUAGES_SELECT_OPTIONS[0]}
            options={LANGUAGES_SELECT_OPTIONS}
            setFieldTouched={formik.setFieldTouched}
            setFieldValue={formik.setFieldValue}
          />
          <i className="glyphicon glyphicon-user form-control-feedback" />
          <FormFeedback>{errors.nativeLanguage}</FormFeedback>
        </FormGroup>
      </div>
      <Row className="mt-4">
        <Col xs={6}>
          <Button className="btn-block c-gray" color="default" onClick={goBack}>
            <i className="btn-icon-back" />
            Back
          </Button>
        </Col>
        <Col xs={6}>
          <Button className="btn-block" type="submit" color="primary" disabled={!isValid}>
            Next
            <i className="btn-icon-next" />
          </Button>
        </Col>
      </Row>
    </Form>
  );
};
